/**
 * Team Store — Manages team membership and admin state
 *
 * Listens to the user's team data and provides team management actions.
 * Team operations call Cloud Functions (never write directly to Firestore).
 */

import { create } from 'zustand';

// ============================================
// Types
// ============================================

export type MemberRole = 'admin' | 'member';

export interface TeamMember {
  uid: string;
  role: MemberRole;
  joinedAt: Date;
  email: string | null;
  displayName: string | null;
}

export interface TeamInfo {
  id: string;
  name: string;
  ownerId: string;
  maxSeats: number;
  memberCount: number;
}

export interface PendingInvitation {
  email: string;
  invitedBy: string;
  createdAt: Date;
}

// ============================================
// Store
// ============================================

interface TeamState {
  // State
  team: TeamInfo | null;
  members: TeamMember[];
  pendingInvitations: PendingInvitation[];
  myRole: MemberRole | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadTeam: (teamId: string) => Promise<void>;
  createTeam: (name: string, maxSeats?: number) => Promise<string>;
  inviteMember: (email: string) => Promise<void>;
  acceptInvitation: (teamId: string) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  updateMemberRole: (memberId: string, role: MemberRole) => Promise<void>;
  leaveTeam: () => Promise<void>;
  clearTeam: () => void;
  clearError: () => void;
}

export const useTeamStore = create<TeamState>((set, get) => ({
  team: null,
  members: [],
  pendingInvitations: [],
  myRole: null,
  isLoading: false,
  error: null,

  loadTeam: async (teamId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { getFirestore } = await import('../lib/firebase');
      const db = await getFirestore();
      const { doc, getDoc, collection, getDocs, orderBy, query } = await import('firebase/firestore');

      // Load team doc
      const teamDoc = await getDoc(doc(db, 'teams', teamId));
      if (!teamDoc.exists()) {
        set({ team: null, isLoading: false });
        return;
      }

      const teamData = teamDoc.data();
      const team: TeamInfo = {
        id: teamId,
        name: teamData.name,
        ownerId: teamData.ownerId,
        maxSeats: teamData.maxSeats,
        memberCount: teamData.memberCount,
      };

      // Load members
      const membersSnap = await getDocs(
        query(collection(db, 'teams', teamId, 'members'), orderBy('joinedAt', 'asc'))
      );

      const members: TeamMember[] = membersSnap.docs.map((d) => {
        const data = d.data();
        return {
          uid: d.id,
          role: data.role,
          joinedAt: data.joinedAt?.toDate?.() || new Date(),
          email: data.email || null,
          displayName: data.displayName || null,
        };
      });

      // Load pending invitations (admin only - check after setting members)
      let pendingInvitations: PendingInvitation[] = [];
      const { useAuthStore } = await import('./authStore');
      const uid = useAuthStore.getState().user?.uid;
      const myMember = members.find((m) => m.uid === uid);

      if (myMember?.role === 'admin') {
        try {
          const invitesSnap = await getDocs(
            collection(db, 'teams', teamId, 'invitations')
          );
          pendingInvitations = invitesSnap.docs
            .filter((d) => d.data().status === 'pending')
            .map((d) => {
              const data = d.data();
              return {
                email: data.email,
                invitedBy: data.invitedBy,
                createdAt: data.createdAt?.toDate?.() || new Date(),
              };
            });
        } catch {
          // Non-admin might not have read access — that's fine
        }
      }

      set({
        team,
        members,
        pendingInvitations,
        myRole: myMember?.role || null,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load team',
        isLoading: false,
      });
    }
  },

  createTeam: async (name: string, maxSeats?: number) => {
    set({ isLoading: true, error: null });
    try {
      const { getFunctions } = await import('../lib/firebase');
      const functions = await getFunctions();
      const { httpsCallable } = await import('firebase/functions');

      const createTeamFn = httpsCallable<
        { name: string; maxSeats?: number },
        { teamId: string; name: string }
      >(functions, 'createTeam');

      const payload: { name: string; maxSeats?: number } = { name };
      if (maxSeats !== undefined) payload.maxSeats = maxSeats;
      const result = await createTeamFn(payload);
      const teamId = result.data.teamId;

      // Load the newly created team
      await get().loadTeam(teamId);
      set({ isLoading: false });

      return teamId;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create team';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  inviteMember: async (email: string) => {
    const { team } = get();
    if (!team) throw new Error('No team loaded');

    set({ error: null });
    try {
      const { getFunctions } = await import('../lib/firebase');
      const functions = await getFunctions();
      const { httpsCallable } = await import('firebase/functions');

      const inviteFn = httpsCallable(functions, 'inviteMember');
      await inviteFn({ teamId: team.id, email });

      // Refresh team data
      await get().loadTeam(team.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to invite member';
      set({ error: message });
      throw error;
    }
  },

  acceptInvitation: async (teamId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { getFunctions } = await import('../lib/firebase');
      const functions = await getFunctions();
      const { httpsCallable } = await import('firebase/functions');

      const acceptFn = httpsCallable(functions, 'acceptInvitation');
      await acceptFn({ teamId });

      // Load the team we just joined
      await get().loadTeam(teamId);
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to accept invitation';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  removeMember: async (memberId: string) => {
    const { team } = get();
    if (!team) throw new Error('No team loaded');

    set({ error: null });
    try {
      const { getFunctions } = await import('../lib/firebase');
      const functions = await getFunctions();
      const { httpsCallable } = await import('firebase/functions');

      const removeFn = httpsCallable(functions, 'removeMember');
      await removeFn({ teamId: team.id, memberId });

      // Refresh team data
      await get().loadTeam(team.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove member';
      set({ error: message });
      throw error;
    }
  },

  updateMemberRole: async (memberId: string, role: MemberRole) => {
    const { team } = get();
    if (!team) throw new Error('No team loaded');

    set({ error: null });
    try {
      const { getFunctions } = await import('../lib/firebase');
      const functions = await getFunctions();
      const { httpsCallable } = await import('firebase/functions');

      const updateFn = httpsCallable(functions, 'updateMemberRole');
      await updateFn({ teamId: team.id, memberId, role });

      // Refresh team data
      await get().loadTeam(team.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update role';
      set({ error: message });
      throw error;
    }
  },

  leaveTeam: async () => {
    const { team } = get();
    if (!team) throw new Error('No team loaded');

    set({ isLoading: true, error: null });
    try {
      const { getFunctions } = await import('../lib/firebase');
      const functions = await getFunctions();
      const { httpsCallable } = await import('firebase/functions');

      const leaveFn = httpsCallable(functions, 'leaveTeam');
      await leaveFn({ teamId: team.id });

      set({
        team: null,
        members: [],
        pendingInvitations: [],
        myRole: null,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to leave team';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  clearTeam: () =>
    set({
      team: null,
      members: [],
      pendingInvitations: [],
      myRole: null,
      isLoading: false,
      error: null,
    }),

  clearError: () => set({ error: null }),
}));

// ============================================
// Selectors
// ============================================

export const selectIsTeamAdmin = (state: TeamState): boolean =>
  state.myRole === 'admin';

export const selectIsTeamOwner = (state: TeamState): boolean => {
  if (!state.team) return false;
  const { useAuthStore } = require('./authStore');
  return state.team.ownerId === useAuthStore.getState().user?.uid;
};

export const selectHasTeam = (state: TeamState): boolean =>
  state.team !== null;
